/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import { useLanguage } from '@/providers/LanguageProvider';
import { formatKahuMessages } from '@/utils/formatKahuMessages';
import { useSearchHighlightText } from '@/ux-common/src/hook/useSearchHighlightText';
import { formatConversationTimestamp } from '@/ux-common/src/utils/formatConversationTimestamp';
import { Conversation } from 'types/kahu';

import { cn } from '@/lib/utils';
import { useKahu } from '@/hooks/useKahu';

import SearchWithNavigation from '../../ux-common/src/components/common/search-with-navigation';
import { CustomButton } from '../common';
import { KahuIcons } from '../icons/icons';

export const KahuConversationList = () => {
  const { messages } = useLanguage();
  const KahuAiString = messages?.KahuAI;

  const {
    conversations,
    switchConversation,
    createNewConversation,
    isLoadingConversations,
    conversationsWithUnreadCounts,
  } = useKahu();

  const {
    term,
    setTerm,
    handleClearSearch,
    prevMatch,
    nextMatch,
    matches,
    currentIndex,
    beginHighlightPass,
    getHighlightedNodes,
  } = useSearchHighlightText(conversations);

  const baseConversations = conversationsWithUnreadCounts;

  const displayedConversations: Conversation[] = term.trim()
    ? baseConversations.filter((conv) =>
        conv.title.toLowerCase().includes(term.toLowerCase())
      )
    : baseConversations;
  // reset refs and match index for this render pass
  beginHighlightPass();

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="py-[10px] pl-3 pr-2">
        <SearchWithNavigation
          value={term}
          onChange={setTerm}
          onClear={handleClearSearch}
          searchWidth="w-full"
          matches={matches}
          currentIndex={currentIndex}
          onPreviousMatch={prevMatch}
          onNextMatch={nextMatch}
          disabled={false}
        />
      </div>

      {/* Get Started card - always on top, not filtered by search */}
      {!term.trim() && (
        <div
          className="flex w-full cursor-pointer items-start gap-3 px-[6px]"
          onClick={createNewConversation}
        >
          <div className="flex w-full items-center gap-[6px] rounded-lg px-2 py-3 hover:bg-[#FFFFFF14]">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FFFFFF14]">
              {KahuIcons.KahuRocketIcon('text-[#8C9EA1]')}
            </span>
            <div className="flex-1">
              <p className="text-body font-normal text-body-subtle ">
                {KahuAiString?.get_started_with_okahu}
              </p>
              <p className="text-body-sm font-normal text-form-input-disabled">
                {KahuAiString?.explore_demos_view_traces_get_insights}..
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Conversation items */}
      <div className="min-h-0 flex-1 overflow-y-auto px-[6px]">
        {isLoadingConversations ? (
          /* Skeleton loader rows */
          <div className="flex flex-col gap-2 px-2 py-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg py-3">
                <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-[#FFFFFF1F]" />
                <div className="flex flex-1 flex-col gap-2">
                  <div className="h-3 w-full animate-pulse rounded bg-[#FFFFFF1F]" />
                  <div className="h-3 w-3/4 animate-pulse rounded bg-[#FFFFFF14]" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          displayedConversations.map((conv) => (
            <button
              key={conv.id}
              onClick={async () => {
                switchConversation(conv.id);
              }}
              className="mb-2 flex w-full cursor-pointer items-start gap-3 rounded-lg text-left hover:bg-[#FFFFFF14]"
            >
              <div className="flex w-full items-center gap-[6px] px-2 py-3">
                <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FFFFFF1F]">
                  {KahuIcons.KahuChatIcon(
                    cn(conv.unreadCount > 0 ? 'text-white' : 'text-[#8C9EA1]')
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'truncate text-sm font-normal text-body-subtle',
                      conv.unreadCount > 0 ? 'font-medium text-white' : ''
                    )}
                  >
                    {getHighlightedNodes(
                      formatKahuMessages(conv.title) || 'New conversation'
                    )}
                  </p>
                  <p
                    className={cn(
                      'truncate text-sm font-normal text-form-input-disabled',
                      conv.unreadCount > 0 ? 'text-body-secondary' : ''
                    )}
                  >
                    {getHighlightedNodes(
                      formatKahuMessages(conv.description) || 'New description'
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1 self-start">
                  <span
                    className={cn(
                      'text-sm font-normal text-body-subtle',
                      conv.unreadCount > 0 ? 'text-[#68E0D1]' : ''
                    )}
                  >
                    {formatConversationTimestamp(conv.updatedAt)}
                  </span>
                  {conv.unreadCount > 0 && (
                    <span className="flex h-[19px] w-[19px] items-center justify-center rounded-full bg-[#34BCAF] text-[11px] font-medium text-white">
                      {conv.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* New Conversation button */}
      <div className="px-3 pb-3 pt-2">
        <CustomButton onClick={createNewConversation} className="w-full">
          + {KahuAiString?.new_conversation}
        </CustomButton>
      </div>
    </div>
  );
};
